<template>
  <div class="p-8">
    <header class="mb-6">
      <h2 class="text-2xl font-bold mb-4">Stakeholders by Category</h2>
      <button 
        @click="addStakeholder" 
        class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        :disabled="!hasCategories"
      >
        Add Stakeholder
      </button>
    </header>

    <div v-if="!hasCategories" class="text-center py-8 text-gray-500">
      No categories available. Please add categories first.
    </div>

    <table v-else class="min-w-full bg-white text-gray-900 rounded shadow">
      <thead>
        <tr>
          <th class="px-4 py-2 text-left">Stakeholder</th>
          <th class="px-4 py-2 text-left">Category</th>
          <th class="px-4 py-2 text-left">Dependency</th>
          <th class="px-4 py-2 text-left">Penetration</th>
          <th class="px-4 py-2 text-left">Maturity</th>
          <th class="px-4 py-2 text-left">Trust</th>
          <th class="px-4 py-2 text-left">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="stakeholder in stakeholders" :key="stakeholder.id">
          <!-- Stakeholder Name (Editable) -->
          <td class="border px-4 py-2">
            <EditableField
              :value="stakeholder.text"
              :is-editing="isEditing(stakeholder.id, 'text')"
              @start-edit="startEdit(stakeholder.id, 'text', stakeholder.text)"
              @save="saveEdit(stakeholder.categoryId, stakeholder.id, 'text', $event)"
              @cancel="cancelEdit"
            />
          </td>

          <!-- Category Selection -->
          <td class="border px-4 py-2">
            <select 
              :value="stakeholder.categoryId" 
              @change="changeCategory(stakeholder.categoryId, stakeholder.id, $event)"
              class="w-full border rounded px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option 
                v-for="category in categories" 
                :key="category.id" 
                :value="category.id"
              >
                {{ category.name }}
              </option>
            </select>
          </td>

          <!-- Numeric Fields -->
          <td class="border px-4 py-2">
            <NumericInput
              :value="stakeholder.dependency"
              @update="updateValue(stakeholder.categoryId, stakeholder.id, 'dependency', $event)"
            />
          </td>
          <td class="border px-4 py-2">
            <NumericInput
              :value="stakeholder.penetration"
              @update="updateValue(stakeholder.categoryId, stakeholder.id, 'penetration', $event)"
            />
          </td>
          <td class="border px-4 py-2">
            <NumericInput
              :value="stakeholder.maturity"
              @update="updateValue(stakeholder.categoryId, stakeholder.id, 'maturity', $event)"
            />
          </td>
          <td class="border px-4 py-2">
            <NumericInput
              :value="stakeholder.trust"
              @update="updateValue(stakeholder.categoryId, stakeholder.id, 'trust', $event)"
            />
          </td>

          <!-- Actions -->
          <td class="border px-4 py-2 text-center">
            <button 
              @click="deleteStakeholder(stakeholder.categoryId, stakeholder.id)"
              class="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              title="Delete stakeholder"
            >
              Delete
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { storeToRefs } from 'pinia';
import { createStakeholderMapStore } from '../stores';
import type { StakeholderData } from '../../../stakeholdersMap/types/StakeholderData';
import EditableField from './EditableField.vue';
import NumericInput from './NumericInput.vue';

// Types
type EditableField = 'text' | 'dependency' | 'penetration' | 'maturity' | 'trust';
type NumericField = 'dependency' | 'penetration' | 'maturity' | 'trust';

interface EditingState {
  id: string;
  field: EditableField;
}

// Store setup
const stakeholderMapStore = createStakeholderMapStore();
const { getAllStakeholders, getAllCategories } = storeToRefs(stakeholderMapStore);

// Reactive state
const editingStakeholder = ref<EditingState | null>(null);
const editingValue = ref<string>('');

// Computed properties
const stakeholders = computed(() => getAllStakeholders.value);
const categories = computed(() => getAllCategories.value);
const hasCategories = computed(() => categories.value.length > 0);

// Methods
const isEditing = (id: string, field: EditableField): boolean => {
  return editingStakeholder.value?.id === id && editingStakeholder.value?.field === field;
};

const startEdit = (id: string, field: EditableField, value: string | number): void => {
  editingStakeholder.value = { id, field };
  editingValue.value = String(value);
  
  nextTick(() => {
    const editInput = document.querySelector('.edit-input') as HTMLInputElement;
    if (editInput) {
      editInput.focus();
      editInput.select();
    }
  });
};

const saveEdit = (categoryId: string, stakeholderId: string, field: EditableField, value: string): void => {
  const stakeholder = stakeholderMapStore.getStakeholderById(categoryId, stakeholderId);
  if (!stakeholder) return;

  let processedValue: string | number = value;
  
  // Validate and convert numeric fields
  if (isNumericField(field)) {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1 || numValue > 4) {
      return; // Invalid value, don't save
    }
    processedValue = numValue;
  }
  
  const updatedStakeholder: StakeholderData = { 
    ...stakeholder, 
    [field]: processedValue 
  };
  
  stakeholderMapStore.updateStakeholder(updatedStakeholder);
  editingStakeholder.value = null;
};

const cancelEdit = (): void => {
  editingStakeholder.value = null;
};

const updateValue = (categoryId: string, stakeholderId: string, field: NumericField, value: number): void => {
  const stakeholder = stakeholderMapStore.getStakeholderById(categoryId, stakeholderId);
  if (!stakeholder) return;

  const updatedStakeholder: StakeholderData = { 
    ...stakeholder, 
    [field]: value 
  };
  
  stakeholderMapStore.updateStakeholder(updatedStakeholder);
};

const addStakeholder = (): void => {
  if (!hasCategories.value) return;

  const category = categories.value[0];
  const newStakeholder = stakeholderMapStore.createDefaultStakeholder(category.id);
  
  stakeholderMapStore.addStakeholderToCategory(category.id, newStakeholder);
};

const deleteStakeholder = (categoryId: string, stakeholderId: string): void => {
  stakeholderMapStore.deleteStakeholder(categoryId, stakeholderId);
};

const changeCategory = (fromCategoryId: string, stakeholderId: string, event: Event): void => {
  const toCategoryId = (event.target as HTMLSelectElement).value;
  if (fromCategoryId !== toCategoryId) {
    stakeholderMapStore.moveStakeholderToCategory(stakeholderId, fromCategoryId, toCategoryId);
  }
};

// Utility functions
const isNumericField = (field: EditableField): field is NumericField => {
  return ['dependency', 'penetration', 'maturity', 'trust'].includes(field);
};

</script>